import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { StarIcon } from "lucide-react";

interface FeedbackFormProps {
  bookingId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FeedbackForm = ({ bookingId, open, onOpenChange }: FeedbackFormProps) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const { toast } = useToast();
  const [hasExistingFeedback, setHasExistingFeedback] = useState(false);

  useEffect(() => {
    const checkExistingFeedback = async () => {
      if (!bookingId) return;
      
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('booking_id', bookingId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking feedback:', error);
      }

      setHasExistingFeedback(!!data);
    };

    checkExistingFeedback();
  }, [bookingId]);

  const handleSubmit = async () => {
    try {
      const { error } = await supabase
        .from('feedback')
        .insert({
          booking_id: bookingId,
          rating,
          comment,
        });

      if (error) throw error;

      toast({
        title: "Thank you for your feedback!",
        description: "Your feedback helps us improve our service.",
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (hasExistingFeedback) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Share Your Experience</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex justify-center space-x-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className="focus:outline-none"
              >
                <StarIcon
                  className={`w-8 h-8 ${
                    star <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
          <Textarea
            placeholder="Share your thoughts about the parking experience..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="min-h-[100px]"
          />
          <Button 
            className="w-full" 
            onClick={handleSubmit}
            disabled={rating === 0}
          >
            Submit Feedback
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackForm;